import collections from '../../../config/config/.collections.json'
import { algoliaAppId, algoliaSearchKey, algoliaIndex, apiDomain, port, useDevAPI } from '../../../config/config/settings'
import axios from 'axios'
import { ref } from 'vue'
import { applyLoginSession, sessionCookieHeaders } from './browserSession.js'
import { installApiCredentials } from './apiCredentials.js'
import { algoliasearch } from 'algoliasearch'
import { useCheckoutStore } from '../stores/store'

let checkoutStore = () => useCheckoutStore()

let controllers = {
	authors: {
		get: () => 'hello world',
		addresses: {
			get: () => 'hello world 2',
			post: () => 'hello world 3',
		},
		'*': () => 'hello world 4',
	},
}

function buildMangoControllers(obj, path = '') {
	const result = {}

	// Collect all methods and check for wildcard '*'
	const methods = {}
	for (const key in obj) {
		if (typeof obj[key] === 'function') {
			methods[key] = obj[key]
		}
	}

	const hasWildcard = methods['*'] !== undefined

	// Define methods for all HTTP verbs
	const httpMethods = ['get', 'post', 'put', 'patch', 'delete', 'options', 'head']

	for (const method of httpMethods) {
		if (methods[method] || hasWildcard) {
			result[method] = () => {
				return new Promise((resolve, reject) => {
					console.log('method', method)
					console.log('`${api}/controllers${path}`', `${api}/controllers${path}`)
					axios[method](`${api}/controllers${path}`)
						.then((response) => resolve(response?.data))
						.catch((e) => reject(e))
				})
			}
			result[method]['init'] = () => {
				let loading = ref(true)
				let data = ref(null)
				let error = ref(null)

				let response = result[method]().then((r) => {
					data.value = r
					loading.value = false
				})

				return { data, loading, error }
			}
		}
	}

	// Recursively build nested controllers
	for (const key in obj) {
		if (typeof obj[key] === 'object' && typeof obj[key] !== 'function') {
			result[key] = buildMangoControllers(obj[key], `${path}/${key}`)
		}
	}

	return result
}

// console.log('collections', collections)

const client = algoliasearch(algoliaAppId, algoliaSearchKey)
// const algolia = client.initIndex(algoliaIndex) // v5 no longer uses initIndex

let api = apiDomain
let ws = `wss://api.bestchoiceroofing.com/graphql`

if (process.env.NODE_ENV != 'production' && useDevAPI) {
	api = `http://localhost:${port}`
	ws = `ws://localhost:${port}/graphql`
}

// Send the httpOnly session cookie on our API only (inhishands.com -> api.inhishands.com).
// Third-party calls, such as the Mailchimp subscribe request, do not get withCredentials.
axios.defaults = axios.defaults || {}
axios.defaults.headers = axios.defaults.headers || {}
axios.defaults.headers.common = axios.defaults.headers.common || {}
installApiCredentials(axios, {
	origins: [api, apiDomain, `http://localhost:${port}`].filter(Boolean),
})

// The host-only Authorization cookie is not sent to the API origin. Read it the
// same way storedAuthorization() does, without importing session.js (that file
// already imports this one). Callers attach the result only to our API.
function authorizationCookie() {
	if (typeof document === 'undefined' || typeof document.cookie !== 'string') return ''
	const match = document.cookie.match(/(?:^|;\s*)Authorization=([^;]*)/)
	return match ? decodeURIComponent(match[1]) : ''
}

export function bearerHeaders() {
	let token = ''
	try {
		token = window.localStorage.getItem('token') || ''
	} catch {
		token = ''
	}
	if (!token || token === 'undefined' || token === 'null') {
		try {
			token = authorizationCookie()
		} catch {
			token = ''
		}
	}
	if (!token || token === 'undefined' || token === 'null') return {}
	return { Authorization: token }
}

function getQuery(params) {
	if (params.search != undefined) params.search = JSON.stringify(params.search)
	if (params.fields != undefined) params.fields = JSON.stringify(params.fields)
	if (params.sort != undefined) params.sort = JSON.stringify(params.sort)

	const query =
		Object.keys(params)
			.filter((key) => params[key] != undefined)
			?.map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
			?.join('&') || ''

	return query
}

const Mango = collections.reduce((a, c) => {
	let runQuery = ({ limit, page, search, fields, id, sort, depthLimit, verbose } = {}) => {
		let fullQuery

		const query = getQuery({
			limit,
			page,
			search,
			fields,
			sort,
			depthLimit,
			verbose,
		})
		// console.log(query)

		fullQuery = `${api}/${c.name}/${id || ''}?${query}`

		return new Promise((resolve, reject) => {
			axios
				.get(fullQuery, { headers: bearerHeaders(), withCredentials: true })
				.then((response) => resolve(verbose ? response?.data : response?.data?.response))
				.catch((e) => reject(e))
		})
	}

	let runGraphql = (query) => {
		query = { query }
		return new Promise((resolve, reject) => {
			axios
				.post(`${api}/graphql`, query, { headers: bearerHeaders(), withCredentials: true })
				.then((response) => resolve(response?.data?.data))
				.catch((e) => reject(e))
		})
	}

	let runAlgolia = (search, query, algoliaFilters) => {
		search = search || ''

		let filters = `collection:${c.name}`
		if (algoliaFilters) filters += ` AND ${algoliaFilters}`

		let algoliaQuery = {
			page: query?.page || 0,
			filters,
			hitsPerPage: query?.limit || 10,
			clickAnalytics: true,
		}

		if (query?.fields) algoliaQuery.attributesToRetrieve = query.fields

		return new Promise((resolve, reject) => {
			algolia.search(search, algoliaQuery).then(({ hits, nbHits, queryID }) => {
				hits.forEach((h) => (h.id = h.objectID))
				resolve({ hits, nbHits, queryID })
			})
		})
	}

	let save = (data, query, verbose) => {
		let { id } = data
		let method = id ? 'put' : 'post'

		data = JSON.parse(JSON.stringify(data))

		// // Remove _id and computed fields
		delete data.collection
		delete data._id
		delete data.id

		for (let field of c.fields) {
			if (field.computed) delete data[field.name]
			if (field.relationship)
				data[field.name] = Array.isArray(data[field.name]) ? data[field.name].map((r) => r.id) : data[field.name]?.id ? data[field.name].id : data[field.name]
		}
		for (let name in data) {
			if (name.includes('__')) delete data[name]
		}

		query = query ? getQuery(query) : ''

		let url = `${api}/${c.name}/${id || ''}?${query}`

		let payload = { ...data }

		return new Promise((resolve, reject) => {
			axios[method](url, payload, {
				headers: bearerHeaders(),
				withCredentials: true,
			})
				.then((response) => {
					// const result = {
					//     ...response?.data,
					//     [Symbol.toPrimitive]: function (hint) {
					//         if (hint === 'string') {
					//             return this.response;
					//         }
					//         return this;
					//     }
					// };
					resolve(verbose ? response?.data : response?.data?.response)
				})
				.catch((e) => reject(e))
		})
	}

	let deleteEntry = (data) => {
		let id = data.id || data

		return new Promise((resolve, reject) => {
			axios
				.delete(`${api}/${c.name}/${id || ''}`, {
					headers: bearerHeaders(),
					withCredentials: true,
				})
				.then((response) => resolve(response?.data))
				.catch((e) => reject(e))
		})
	}

	a[c.name] = runQuery
	a[c.name]['save'] = save
	a[c.name]['delete'] = deleteEntry
	a[c.singular] = (id, query) => runQuery({ id, ...query })

	a[c.name]['search'] = runAlgolia
	a[c.name]['search']['init'] = (search, query, algoliaFilters) => {
		let loading = ref(true)
		let data = ref(null)
		let error = ref(null)
		let totalResults = ref(null)
		let searchQueryID = ref(null)

		let response = runAlgolia(search, query, algoliaFilters)
			.then((response) => {
				console.log('algolia init response: ', response)
				data.value = response.hits
				totalResults.value = response.nbHits
				searchQueryID.value = response.queryID
				loading.value = false
			})
			.catch((e) => {
				loading.value = false
				error.value = e
			})

		return { data, loading, error, queryId: searchQueryID }
	}

	a[c.name]['init'] = ({ limit, page, search, fields, id, sort } = {}) => {
		let loading = ref(true)
		let data = ref(null)
		let error = ref(null)

		let response = runQuery({ limit, page, search, fields, id, sort })
			.then((response) => {
				data.value = response
				loading.value = false
			})
			.catch((e) => {
				loading.value = false
				error.value = e
			})

		return { data, loading, error }
	}

	a[c.singular]['init'] = (id) => a[c.name]['init']({ id })

	a.relationRequest = ({ limit, page, search, fields, id, sort, depthLimit, path } = {}) => {
		let fullQuery

		const params = { limit, page, search, fields, sort, depthLimit }

		if (params.search != undefined) params.search = JSON.stringify(params.search)
		if (params.fields != undefined) params.fields = JSON.stringify(params.fields)
		if (params.sort != undefined) params.sort = JSON.stringify(params.sort)

		const query =
			Object.keys(params)
				.filter((key) => params[key] != undefined)
				?.map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
				?.join('&') || ''
		// console.log(query)

		fullQuery = `${api}/${path}?${query}`

		return new Promise((resolve, reject) => {
			axios
				.get(fullQuery, { headers: bearerHeaders(), withCredentials: true })
				.then((response) => resolve(response?.data?.response))
				.catch((e) => reject(e))
		})
	}
	a.relationRequest.init = (query) => {
		let loading = ref(true)
		let data = ref(null)
		let error = ref(null)

		let response = a
			.relationRequest(query)
			.then((response) => {
				data.value = response
				loading.value = false
			})
			.catch((e) => {
				loading.value = false
				error.value = e
			})

		return { data, loading, error }
	}

	a.graphql = runGraphql
	a.graphql.init = (query) => {
		let loading = ref(true)
		let data = ref(null)
		let error = ref(null)

		let response = runGraphql(query)
			.then((response) => {
				data.value = response
				loading.value = false
			})
			.catch((e) => {
				loading.value = false
				error.value = e
			})

		return { data, loading, error }
	}

	return a
}, {})

async function enrichCallForPricingProducts(hits) {
	const needsEnrichment = hits.filter(
		(h) => h.collection === 'products' && h.outOfStock && h.isPriceHidden == null && h.priceHiddenLabel == null,
	)
	if (!needsEnrichment.length) return hits

	try {
		const ids = needsEnrichment.map((h) => h.id)
		const products = await Mango.products({
			search: { id: { $in: ids } },
			fields: ['id', 'isPriceHidden', 'priceHiddenLabel'],
			limit: ids.length,
		})
		const byId = Object.fromEntries(products.map((p) => [p.id, p]))
		return hits.map((h) => {
			const pricing = byId[h.id]
			if (!pricing) return h
			return {
				...h,
				isPriceHidden: pricing.isPriceHidden,
				priceHiddenLabel: pricing.priceHiddenLabel,
			}
		})
	} catch (error) {
		console.error('Failed to enrich call-for-pricing products from search results', error)
		return hits
	}
}

Mango.search = async (search, query, algoliaFilters) => {
	// let algoliaQuery = {
	// 	page: query?.page || 0,
	// 	filters,
	// 	hitsPerPage: query?.limit || 10,
	// 	clickAnalytics: true,
	// }

	// if (query?.fields) algoliaQuery.attributesToRetrieve = query.fields

	// return new Promise((resolve, reject) => {
	// 	algolia.search(search, algoliaQuery).then(({ hits, queryID }) => {
	// 		hits.forEach((h) => (h.id = h.objectID))
	// 		resolve({ hits, queryID })
	// 	})
	// })

	const q = search || ''
	const filters = algoliaFilters || ''

	const searchParams = {
		query: q,
		page: query?.page ?? 0,
		hitsPerPage: query?.limit ?? 10,
		clickAnalytics: true,
		...(filters ? { filters } : {}),
		...(query?.fields ? { attributesToRetrieve: query.fields } : {}),
	}

	const { hits, queryID } = await client.searchSingleIndex({ indexName: algoliaIndex, searchParams })
	hits.forEach((h) => (h.id = h.objectID))
	const enrichedHits = await enrichCallForPricingProducts(hits)
	return { hits: enrichedHits, queryID }
}

Mango.login = ({ email, password }) => {
	return new Promise((resolve, reject) => {
		axios
			.post(`${api}/controllers/account/login`, { email, password }, { withCredentials: true, headers: sessionCookieHeaders() })
			.then(async (response) => {
				if (response.data?.success && response.data?.memberId) {
					await applyLoginSession({
						memberId: response.data.memberId,
						email,
						token: response.data.token,
						axios,
					})
				}
				resolve(response.data)
			})
			.catch((e) => {
				const data = e?.response?.data
				if (data?.success === false && data?.code === 'session_store_unavailable') {
					resolve(data)
					return
				}
				reject(e)
			})
	})
}

Mango.endpoints = buildMangoControllers(controllers)

Mango.collections = collections
Mango.ws = ws

Mango.upload = async (file) => {
	return new Promise((resolve, reject) => {
		const formData = new FormData()

		let uploading = true
		let filename = file.name
		let progress = 0
		let url
		let error

		// // Compress the image
		// if (file.type.includes('image')) {
		//     let results = await compress.compress([file], {
		//         quality: .75, // the quality of the image, max is 1,
		//         maxWidth: 1920, // the max width of the output image, defaults to 1920px
		//         maxHeight: 1920, // the max height of the output image, defaults to 1920px
		//         resize: true, // defaults to true, set false if you do not want to resize the image width and height
		//         rotate: false, // See the rotation section below
		//     })
		//     const img1 = results[0]
		//     const base64str = img1.data
		//     const imgExt = img1.ext
		//     const filename = file.name
		//     file = Compress.convertBase64ToFile(base64str, imgExt)
		//     file = new File([file], filename, { type: file.type });
		//     console.log('file', file)
		// }

		formData.append('file', file)

		const xhr = new XMLHttpRequest()

		xhr.open('POST', `${api}/upload`, true)
		xhr.withCredentials = true

		xhr.upload.onprogress = (event) => {
			if (event.lengthComputable) {
				progress = (event.loaded / event.total) * 100
			}
		}

		xhr.onload = () => {
			if (xhr.status === 200) {
				const json = JSON.parse(xhr.response)
				const path = json.paths[0]
				const url = api + path
				uploading = false
				progress = 0
				resolve(url)
			} else {
				error = 'Error while uploading file'
				uploading = false
				reject(error)
			}
		}

		xhr.onerror = () => {
			error = 'Error while uploading file'
			uploading = false
			reject(error)
		}

		xhr.send(formData)
	})
}

Mango.online = async () => {
	try {
		return (await axios.get(`${api}/controllers/test`))?.data?.includes('🥭')
	} catch (e) {
		return false
	}
}

/** Drop any Authorization header this client left on the shared axios instance. */
export function clearMangoAuthorization() {
	delete axios.defaults.headers.common['Authorization']
	delete axios.defaults.headers.common['authorization']
}

Mango.clearAuthorization = clearMangoAuthorization

export default Mango
